import {Scope} from "./Scope";

export enum Permission {
    SOURCE__READ_SEAL = "SOURCE__READ_SEAL",
    SOURCE__READ_ORG = "SOURCE__READ_ORG",
    SOURCE__MANAGE_ORG = "SOURCE__MANAGE_ORG",

    TICKET__MANAGE = "TICKET__MANAGE",

    VIEW__READ_SEAL = "VIEW__READ_SEAL",
    VIEW__READ_ORG = "VIEW__READ_ORG",
    VIEW__MANAGE_ORG = "VIEW__MANAGE_ORG",

    REPORT__READ = "REPORT__READ",
    REPORT__MANAGE = "REPORT__MANAGE",

    ITEM_UPDATE_PROFILE__READ_SEAL = "ITEM_UPDATE_PROFILE__READ_SEAL",
    ITEM_UPDATE_PROFILE__READ_ORG = "ITEM_UPDATE_PROFILE__READ_ORG",
    ITEM_UPDATE_PROFILE__MANAGE_ORG = "ITEM_UPDATE_PROFILE__MANAGE_ORG",

    AI_EXTRACTOR__READ_SEAL = "AI_EXTRACTOR__READ_SEAL",
    AI_EXTRACTOR__READ_ORG = "AI_EXTRACTOR__READ_ORG",
    AI_EXTRACTOR__MANAGE_ORG = "AI_EXTRACTOR__MANAGE_ORG",

    AI_REPORT_SUMMARIZER__READ_SEAL = "AI_REPORT_SUMMARIZER__READ_SEAL",
    AI_REPORT_SUMMARIZER__READ_ORG = "AI_REPORT_SUMMARIZER__READ_ORG",
    AI_REPORT_SUMMARIZER__MANAGE_ORG = "AI_REPORT_SUMMARIZER__MANAGE_ORG",

    AI_PROMPT__READ_SEAL = "AI_PROMPT__READ_SEAL",
    AI_PROMPT__READ_ORG = "AI_PROMPT__READ_ORG",
    AI_PROMPT__MANAGE_ORG = "AI_PROMPT__MANAGE_ORG",

    AI_ATTRIBUTE__READ_SEAL = "AI_ATTRIBUTE__READ_SEAL",
    AI_ATTRIBUTE__READ_ORG = "AI_ATTRIBUTE__READ_ORG",
    AI_ATTRIBUTE__MANAGE_ORG = "AI_ATTRIBUTE__MANAGE_ORG",

    INTEREST__READ_ORG = "INTEREST__READ_ORG",
    INTEREST__MANAGE_ORG = "INTEREST__MANAGE_ORG",

    ATTRIBUTE__READ = "ATTRIBUTE__READ",

    SBOM_PROJECT__READ = "SBOM_PROJECT__READ",
    SBOM_PROJECT__ANALYZE = "SBOM_PROJECT__ANALYZE",
    SBOM_PROJECT__MANAGE = "SBOM_PROJECT__MANAGE",
    SBOM_PROJECT__INGEST = "SBOM_PROJECT__INGEST",
    SBOM_PROJECT__MANAGE_ORG = "SBOM_PROJECT__MANAGE_ORG",

    USER__MANAGE = "USER__MANAGE"
}

export function withTransitivePermissions(permission: Permission) {
    return [
        permission,
        ...transitivePermissions(permission)
    ];
}

export function transitivePermissions(permission: Permission) {
    const visited = new Set<Permission>();

    visited.add(permission);

    collectTransitivePermissions(permission, visited);

    visited.delete(permission);

    return Array.from(visited);
}

function collectTransitivePermissions(permission: Permission, visited: Set<Permission>) {
    for (const transitivePermission of directTransitivePermissions(permission)) {
        if (visited.has(transitivePermission)) {
            continue;
        }

        visited.add(transitivePermission);
        collectTransitivePermissions(transitivePermission, visited);
    }
}

function directTransitivePermissions(permission: Permission) {
    switch (permission) {
        case Permission.SOURCE__READ_SEAL:
        case Permission.SOURCE__READ_ORG:
            return [];
        case Permission.SOURCE__MANAGE_ORG:
            return [Permission.SOURCE__READ_ORG];
        case Permission.TICKET__MANAGE:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.VIEW__READ_SEAL:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.VIEW__READ_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.VIEW__MANAGE_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.VIEW__READ_SEAL, Permission.VIEW__READ_ORG];
        case Permission.REPORT__READ:
            return [
                Permission.SOURCE__READ_SEAL,
                Permission.SOURCE__READ_ORG,
                Permission.VIEW__READ_SEAL,
                Permission.VIEW__READ_ORG,
                Permission.ITEM_UPDATE_PROFILE__READ_SEAL,
                Permission.ITEM_UPDATE_PROFILE__READ_ORG
            ];
        case Permission.REPORT__MANAGE:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.VIEW__READ_SEAL, Permission.VIEW__READ_ORG, Permission.REPORT__READ];
        case Permission.ITEM_UPDATE_PROFILE__READ_SEAL:
            return [Permission.SOURCE__READ_SEAL];
        case Permission.ITEM_UPDATE_PROFILE__READ_ORG:
            return [Permission.SOURCE__READ_ORG];
        case Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG:
            return [Permission.ITEM_UPDATE_PROFILE__READ_SEAL, Permission.ITEM_UPDATE_PROFILE__READ_ORG];
        case Permission.AI_PROMPT__READ_SEAL:
        case Permission.AI_PROMPT__READ_ORG:
            return [];
        case Permission.AI_PROMPT__MANAGE_ORG:
            return [Permission.AI_PROMPT__READ_SEAL, Permission.AI_PROMPT__READ_ORG];
        case Permission.AI_EXTRACTOR__READ_SEAL:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.AI_ATTRIBUTE__READ_SEAL, Permission.AI_PROMPT__READ_SEAL];
        case Permission.AI_EXTRACTOR__READ_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.AI_ATTRIBUTE__READ_ORG, Permission.AI_PROMPT__READ_ORG];
        case Permission.AI_EXTRACTOR__MANAGE_ORG:
            return [
                Permission.SOURCE__READ_SEAL,
                Permission.SOURCE__READ_ORG,
                Permission.AI_EXTRACTOR__READ_SEAL,
                Permission.AI_EXTRACTOR__READ_ORG,
                Permission.AI_ATTRIBUTE__MANAGE_ORG,
                Permission.AI_PROMPT__MANAGE_ORG,
                Permission.ITEM_UPDATE_PROFILE__READ_SEAL,
                Permission.ITEM_UPDATE_PROFILE__READ_ORG
            ];
        case Permission.AI_REPORT_SUMMARIZER__READ_SEAL:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.AI_PROMPT__READ_SEAL];
        case Permission.AI_REPORT_SUMMARIZER__READ_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.AI_PROMPT__READ_ORG];
        case Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG:
            return [
                Permission.SOURCE__READ_SEAL,
                Permission.SOURCE__READ_ORG,
                Permission.AI_REPORT_SUMMARIZER__READ_SEAL,
                Permission.AI_REPORT_SUMMARIZER__READ_ORG,
                Permission.AI_PROMPT__MANAGE_ORG
            ];
        case Permission.INTEREST__READ_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.INTEREST__MANAGE_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.INTEREST__READ_ORG];
        case Permission.AI_ATTRIBUTE__READ_SEAL:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.AI_ATTRIBUTE__READ_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.AI_ATTRIBUTE__MANAGE_ORG:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG, Permission.AI_ATTRIBUTE__READ_SEAL, Permission.AI_ATTRIBUTE__READ_ORG];
        case Permission.ATTRIBUTE__READ:
            return [Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG];
        case Permission.SBOM_PROJECT__READ:
            return [];
        case Permission.SBOM_PROJECT__ANALYZE:
            return [Permission.SBOM_PROJECT__READ];
        case Permission.SBOM_PROJECT__MANAGE:
            return [Permission.SBOM_PROJECT__READ, Permission.SBOM_PROJECT__ANALYZE];
        case Permission.SBOM_PROJECT__INGEST:
            return [];
        case Permission.SBOM_PROJECT__MANAGE_ORG:
            return [];
        case Permission.USER__MANAGE:
            return [Permission.SBOM_PROJECT__MANAGE_ORG];
    }
}

export function scopesForPermission(permission: Permission) {
    switch (permission) {
        case Permission.SOURCE__READ_SEAL:
        case Permission.SOURCE__READ_ORG:
        case Permission.SOURCE__MANAGE_ORG:
        case Permission.TICKET__MANAGE:
        case Permission.VIEW__READ_SEAL:
        case Permission.VIEW__READ_ORG:
        case Permission.VIEW__MANAGE_ORG:
        case Permission.AI_EXTRACTOR__READ_SEAL:
        case Permission.AI_EXTRACTOR__READ_ORG:
        case Permission.AI_EXTRACTOR__MANAGE_ORG:
        case Permission.AI_REPORT_SUMMARIZER__READ_SEAL:
        case Permission.AI_REPORT_SUMMARIZER__READ_ORG:
        case Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG:
        case Permission.AI_PROMPT__READ_SEAL:
        case Permission.AI_PROMPT__READ_ORG:
        case Permission.AI_PROMPT__MANAGE_ORG:
        case Permission.INTEREST__READ_ORG:
        case Permission.INTEREST__MANAGE_ORG:
        case Permission.AI_ATTRIBUTE__READ_SEAL:
        case Permission.AI_ATTRIBUTE__READ_ORG:
        case Permission.AI_ATTRIBUTE__MANAGE_ORG:
        case Permission.ITEM_UPDATE_PROFILE__READ_SEAL:
        case Permission.ITEM_UPDATE_PROFILE__READ_ORG:
        case Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG:
        case Permission.REPORT__READ:
        case Permission.REPORT__MANAGE:
        case Permission.ATTRIBUTE__READ:
        case Permission.USER__MANAGE:
        case Permission.SBOM_PROJECT__MANAGE_ORG:
            return [];
        case Permission.SBOM_PROJECT__READ:
        case Permission.SBOM_PROJECT__ANALYZE:
        case Permission.SBOM_PROJECT__MANAGE:
            return [Scope.SBOM_PROJECT];
        case Permission.SBOM_PROJECT__INGEST:
            return [Scope.SBOM_PROJECT_GROUP];
    }
}

export function formatPermission(permission: Permission) {
    switch (permission) {
        case Permission.SOURCE__READ_SEAL:
            return 'Read SEAL vulnerability sources';
        case Permission.SOURCE__READ_ORG:
            return 'Read organization vulnerability sources';
        case Permission.SOURCE__MANAGE_ORG:
            return 'Manage organization vulnerability sources';

        case Permission.TICKET__MANAGE:
            return 'Manage tickets';

        case Permission.VIEW__READ_SEAL:
            return 'Read SEAL vulnerability views';
        case Permission.VIEW__READ_ORG:
            return 'Read organization vulnerability views';
        case Permission.VIEW__MANAGE_ORG:
            return 'Manage organization vulnerability views';

        case Permission.REPORT__READ:
            return 'Read reports';
        case Permission.REPORT__MANAGE:
            return 'Manage reports';

        case Permission.ITEM_UPDATE_PROFILE__READ_SEAL:
            return 'Read SEAL item update profiles';
        case Permission.ITEM_UPDATE_PROFILE__READ_ORG:
            return 'Read organization item update profiles';
        case Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG:
            return 'Manage organization item update profiles';

        case Permission.AI_EXTRACTOR__READ_SEAL:
            return 'Read SEAL AI extractors';
        case Permission.AI_EXTRACTOR__READ_ORG:
            return 'Read organization AI extractors';
        case Permission.AI_EXTRACTOR__MANAGE_ORG:
            return 'Manage organization AI extractors';

        case Permission.AI_REPORT_SUMMARIZER__READ_SEAL:
            return 'Read SEAL AI report summarizers';
        case Permission.AI_REPORT_SUMMARIZER__READ_ORG:
            return 'Read organization AI report summarizers';
        case Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG:
            return 'Manage organization AI report summarizers';

        case Permission.AI_PROMPT__READ_SEAL:
            return 'Read SEAL AI prompts';
        case Permission.AI_PROMPT__READ_ORG:
            return 'Read organization AI prompts';
        case Permission.AI_PROMPT__MANAGE_ORG:
            return 'Manage organization AI prompts';

        case Permission.INTEREST__READ_ORG:
            return 'Read organization interests';
        case Permission.INTEREST__MANAGE_ORG:
            return 'Manage organization interests';

        case Permission.AI_ATTRIBUTE__READ_SEAL:
            return 'Read SEAL AI attributes';
        case Permission.AI_ATTRIBUTE__READ_ORG:
            return 'Read organization AI attributes';
        case Permission.AI_ATTRIBUTE__MANAGE_ORG:
            return 'Manage organization AI attributes';

        case Permission.ATTRIBUTE__READ:
            return 'View attributes and attribute definitions';

        case Permission.SBOM_PROJECT__READ:
            return 'Read project';
        case Permission.SBOM_PROJECT__ANALYZE:
            return 'Analyze project';
        case Permission.SBOM_PROJECT__MANAGE:
            return 'Manage project';
        case Permission.SBOM_PROJECT__INGEST:
            return 'Ingest projects';
        case Permission.SBOM_PROJECT__MANAGE_ORG:
            return 'Manage organization projects';

        case Permission.USER__MANAGE:
            return 'Manage users';
    }

}
